/**
 * Membership Utility Functions
 * 
 * Shared helper functions for displaying user membership plans
 * across all product cards, company cards, and modals.
 */

// Plan Icons - SVG Data URIs for consistent display
export const PLAN_ICONS = {
  // Silver/Basic icon
  basic: '/uploads/builder-images/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea_w44.png',
  // Gold/GCC Business icon
  gcc: '/uploads/builder-images/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c_w44.png',
  // Platinum/Global Enterprise icon
  global: '/uploads/builder-images/050559d54da8d35ba6d11841efdb737d4c3ae660_w44.png'
};

// Plan Colors
export const PLAN_COLORS = {
  basic: '#6D6D6D',
  gcc: '#009D0A',
  global: '#009D0A'
};

/**
 * Get membership plan configuration based on plan name
 * 
 * @param {string} plan - The membership plan name from database
 *                       (Basic, Premium, GCC Business, Global, Enterprise, etc.)
 * @returns {Object} Plan config with { color, icon, label, planType }
 * 
 * @example
 * const config = getPlanConfig('GCC Business');
 * // Returns: { color: '#009D0A', icon: '...', label: 'GCC Business', planType: 'gcc' }
 */
export const getPlanConfig = (plan) => {
  const planLower = (plan || 'Basic').toLowerCase().trim();
  
  // Check for GCC/Premium/Gold/Professional plans
  if (planLower.includes('gcc') || planLower.includes('premium') || planLower.includes('gold') || planLower === 'professional') {
    return { 
      color: PLAN_COLORS.gcc, 
      icon: PLAN_ICONS.gcc, 
      label: 'GCC',
      planType: 'gcc'
    };
  }
  
  // Check for Global/Enterprise/Platinum plans
  if (planLower.includes('global') || planLower.includes('enterprise') || planLower.includes('platinum')) {
    return { 
      color: PLAN_COLORS.global, 
      icon: PLAN_ICONS.global, 
      label: 'Global',
      planType: 'global'
    };
  }
  
  // Default to Basic
  return { 
    color: PLAN_COLORS.basic, 
    icon: PLAN_ICONS.basic, 
    label: 'Basic',
    planType: 'basic'
  };
};

/**
 * Check if verified badge should be displayed based on membership plan
 * Only GCC and Global plans should show verified badge
 * 
 * @param {string} plan - The membership plan name
 * @returns {boolean} Whether to show verified badge
 */
export const shouldShowVerifiedBadge = (plan) => {
  const config = getPlanConfig(plan);
  // Only GCC and Global plans show verified badge, NOT Basic
  return config.planType === 'gcc' || config.planType === 'global';
};

/**
 * Check if user can reply to messages based on membership plan
 * Only GCC and Global plans can reply, Basic can only view
 * 
 * @param {string} plan - The membership plan name
 * @returns {boolean} Whether user can reply to messages
 */
export const canReplyToMessages = (plan) => {
  const config = getPlanConfig(plan);
  // Only GCC and Global plans can reply to messages
  return config.planType === 'gcc' || config.planType === 'global';
};

/**
 * Get seller's membership plan from various data sources
 * 
 * @param {Object} product - Product object
 * @param {Object} seller - Seller object (optional, can be nested in product)
 * @returns {string} The membership plan name
 */
export const getSellerMembershipPlan = (product, seller = null) => {
  // Priority order for finding membership plan
  const sellerData = seller || product?.seller || {};
  
  return sellerData.membershipPlan || 
         product?.membershipPlan || 
         sellerData.plan ||
         product?.plan ||
         'Basic';
};

/**
 * Check if plan should show a badge (for hiding Basic if needed)
 * 
 * @param {string} plan - The membership plan name
 * @returns {boolean} Whether to show the badge
 */
export const shouldShowPlanBadge = (plan) => {
  const config = getPlanConfig(plan);
  // Show badge for all plans (change to exclude 'basic' if you want to hide Basic badges)
  return true;
  // Alternative: return config.planType !== 'basic';
};

/**
 * Check if user can send RFQ (Request for Quote) based on membership plan
 * ALL plans (Basic, GCC, Global) can send RFQs
 * 
 * @param {string} plan - The membership plan name
 * @returns {boolean} Whether user can send RFQ - always true for all plans
 */
export const canSendRFQ = (plan) => {
  // All plans can send RFQs - Basic, GCC, and Global
  return true;
};

/**
 * Check if user can send inquiries/messages based on membership plan
 * Basic plan users CANNOT send messages or inquiries
 * Only GCC and Global plans can send
 * 
 * @param {string} plan - The membership plan name
 * @returns {boolean} Whether user can send inquiries/messages
 */
export const canSendInquiry = (plan) => {
  const config = getPlanConfig(plan);
  // Only GCC and Global plans can send inquiries/messages
  // Basic plan users CANNOT send messages
  return config.planType === 'gcc' || config.planType === 'global';
};

export default {
  getPlanConfig,
  getSellerMembershipPlan,
  shouldShowPlanBadge,
  shouldShowVerifiedBadge,
  canReplyToMessages,
  canSendInquiry,
  canSendRFQ,
  PLAN_ICONS,
  PLAN_COLORS
};
